import { assertNever } from '@tock/shared/utils/typeUtils';
/*
NOTE(jacob, 2019-10-28): Google Maps won't take SVGs for marker icons in normal
ways -- you can't give it a URL of an SVG, for example, or give it an svg DOM
element. You _can_, however, give it a data:image URL. So that's what we do
here. If we find that we want to do a bunch of this for whatever reason, it
would be good to set up a build step to slurp in an SVG file and turn it into a
data URL, but since this is probably a complete one-off I didn't bother.

Technique found here: https://robert.katzki.de/posts/inline-svg-as-google-maps-marker
*/
export function mapPinIcon(scaledSize: number | undefined, color = '#2A2AE9'): google.maps.Icon {
  return {
    url:
      'data:image/svg+xml;base64,' +
      btoa(
        `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\
<path d="M12 13.4779C9.23858 13.4779 7 11.2428 7 8.48555C7 5.72833 9.23858 3.49316 12 3.49316C14.7614 3.49316 17 5.72833 17 8.48555C17 11.2428 14.7614 13.4779 12 13.4779Z" fill="white"/>\
<path fill-rule="evenodd" clip-rule="evenodd" d="M4 7.98781C4.00496 3.57832 7.58378 0.00495233 12 0C16.4162 0.00495233 19.995 3.57832 20 7.98781C20 11.4965 15 19.9955 12.852 23.4872C12.6715 23.7843 12.3481 23.9651 12 23.9634C11.6526 23.9635 11.33 23.7836 11.148 23.4882C9 19.9945 4 11.4925 4 7.98781ZM8.5 7.98781C8.5 9.91786 10.067 11.4825 12 11.4825C13.933 11.4825 15.5 9.91786 15.5 7.98781C15.5 6.05776 13.933 4.49314 12 4.49314C10.067 4.49314 8.5 6.05776 8.5 7.98781Z" fill="${color}"/>\
</svg>`
      ),
    scaledSize: scaledSize
      ? new google.maps.Size(scaledSize, scaledSize)
      : new google.maps.Size(24, 24),
  };
}

interface MapIcon {
  icon: string;
}

const restaurantEnabled: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25446)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#2A2AE9"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#344EF4"/>
  <path d="M23.2764 11.125L23.2764 23.875H21.7764V20.2321H21L20.25 19.4821L20.2501 13.375C20.2501 12.1323 21.2574 11.125 22.5001 11.125H23.2764Z" fill="white"/>
  <path d="M12 11.875V14.875C12 16.2729 12.9561 17.4475 14.25 17.7805V23.875H15.75V17.7805C17.0439 17.4475 18 16.2729 18 14.875V11.875H16.5V14.875C16.5 15.4302 16.1984 15.915 15.75 16.1743V11.875H14.25V16.1743C13.8016 15.915 13.5 15.4302 13.5 14.875V11.875H12Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25446">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>`,
};

const restaurantSelected: MapIcon = {
  icon: `<svg width="40" height="46" viewBox="0 0 40 46" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25437)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24.2245 39.553C33.2414 37.6142 40 29.5964 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 29.9295 7.23602 38.1689 16.7225 39.7328L20.4191 45.8382L24.2245 39.553Z" fill="#2B2B2B"/>
  <path d="M26.2593 12.91L26.2593 27.0767H24.5926V23.029H23.73L22.8967 22.1957L22.8967 15.41C22.8968 14.0293 24.016 12.91 25.3967 12.91H26.2593Z" fill="white"/>
  <path d="M13.73 13.7433V17.0767C13.73 18.6299 14.7923 19.935 16.23 20.305V27.0767H17.8967V20.305C19.3344 19.935 20.3967 18.6299 20.3967 17.0767V13.7433H18.73V17.0767C18.73 17.6936 18.3948 18.2322 17.8967 18.5204V13.7433H16.23V18.5204C15.7318 18.2322 15.3967 17.6936 15.3967 17.0767V13.7433H13.73Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25437">
  <rect width="40" height="46" fill="white"/>
  </clipPath>
  </defs>
  </svg>`,
};

const restaurantVisited: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25441)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#0A0A7B"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#0F0FC0"/>
  <path d="M23.2764 11.125L23.2764 23.875H21.7764V20.2321H21L20.25 19.4821L20.2501 13.375C20.2501 12.1323 21.2574 11.125 22.5001 11.125H23.2764Z" fill="white"/>
  <path d="M12 11.875V14.875C12 16.2729 12.9561 17.4475 14.25 17.7805V23.875H15.75V17.7805C17.0439 17.4475 18 16.2729 18 14.875V11.875H16.5V14.875C16.5 15.4302 16.1984 15.915 15.75 16.1743V11.875H14.25V16.1743C13.8016 15.915 13.5 15.4302 13.5 14.875V11.875H12Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25441">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>`,
};

const barEnabled: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25397)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#2A2AE9"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#344EF4"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0401 11L11.48 12.2488L17.2444 18.7213V22.9355H15.6987V24.4355H20.1987V22.9355H18.7444V18.7213L24.5088 12.2488L23.9487 11H12.0401ZM22.2764 12.5L17.9944 17.308L13.7123 12.5H22.2764Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25397">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const barSelected: MapIcon = {
  icon: `<svg width="40" height="46" viewBox="0 0 40 46" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25382)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24.2245 39.553C33.2414 37.6142 40 29.5964 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 29.9295 7.23602 38.1689 16.7225 39.7328L20.4191 45.8382L24.2245 39.553Z" fill="#2B2B2B"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3823 12.6L12.76 13.9876L19.1649 21.1792V25.8617H17.4475V27.5284H22.4475V25.8617H20.8316V21.1792L27.2364 13.9876L26.6141 12.6H13.3823ZM24.756 14.2667L19.9982 19.6089L15.2404 14.2667H24.756Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25382">
  <rect width="40" height="46" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const barVisited: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25389)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#0A0A7B"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#0F0FC0"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0401 11L11.48 12.2488L17.2444 18.7213V22.9355H15.6987V24.4355H20.1987V22.9355H18.7444V18.7213L24.5088 12.2488L23.9487 11H12.0401ZM22.2764 12.5L17.9944 17.308L13.7123 12.5H22.2764Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25389">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const otherEnabled: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25377)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#2A2AE9"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#344EF4"/>
  <path d="M18.75 18.25H21.75V20.5H18.75V18.25Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.25 16.1247C10.7959 15.7274 10.5 15.1636 10.5 14.5V14.3965L11.5256 10.75H24.4744L25.5 14.3965V14.5C25.5 15.1636 25.2041 15.7275 24.75 16.1247V24.25L11.25 24.25V16.1247ZM23.3381 12.25L23.9937 14.581C23.9451 14.8973 23.6177 15.25 23.0625 15.25C22.4592 15.25 22.125 14.8336 22.125 14.5H20.625C20.625 14.8336 20.2908 15.25 19.6875 15.25C19.0842 15.25 18.75 14.8336 18.75 14.5H17.25C17.25 14.8336 16.9158 15.25 16.3125 15.25C15.7092 15.25 15.375 14.8336 15.375 14.5H13.875C13.875 14.8336 13.5408 15.25 12.9375 15.25C12.3823 15.25 12.0549 14.8973 12.0063 14.581L12.6619 12.25L23.3381 12.25ZM12.75 16.7432V22.75H14.25V19H17.25V22.75L23.25 22.75V16.7432C23.1878 16.7477 23.1253 16.75 23.0625 16.75C22.434 16.75 21.8264 16.5197 21.375 16.1247C20.9236 16.5197 20.316 16.75 19.6875 16.75C19.059 16.75 18.4514 16.5197 18 16.1247C17.5486 16.5197 16.941 16.75 16.3125 16.75C15.684 16.75 15.0764 16.5197 14.625 16.1247C14.1736 16.5197 13.566 16.75 12.9375 16.75C12.8747 16.75 12.8122 16.7477 12.75 16.7432Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25377">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const otherSelected: MapIcon = {
  icon: `<svg width="40" height="46" viewBox="0 0 40 46" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25368)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24.2245 39.553C33.2414 37.6142 40 29.5964 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 29.9295 7.23602 38.1689 16.7225 39.7328L20.4191 45.8382L24.2245 39.553Z" fill="#2B2B2B"/>
  <path d="M20.8367 20.8333H24.17V23.3333H20.8367V20.8333Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.5033 18.4719C11.9988 18.0305 11.67 17.404 11.67 16.6667V16.5517L12.8095 12.5H27.1971L28.3367 16.5517V16.6667C28.3367 17.404 28.0078 18.0305 27.5033 18.4719V27.5L12.5033 27.5V18.4719ZM25.9345 14.1667L26.663 16.7567C26.609 17.1081 26.2453 17.5 25.6283 17.5C24.9581 17.5 24.5867 17.0373 24.5867 16.6667H22.92C22.92 17.0373 22.5486 17.5 21.8783 17.5C21.2081 17.5 20.8367 17.0373 20.8367 16.6667H19.17C19.17 17.0373 18.7986 17.5 18.1283 17.5C17.4581 17.5 17.0867 17.0373 17.0867 16.6667H15.42C15.42 17.0373 15.0486 17.5 14.3783 17.5C13.7614 17.5 13.3977 17.1081 13.3437 16.7567L14.0721 14.1667L25.9345 14.1667ZM14.17 19.1591V25.8333H15.8367V21.6667H19.17V25.8333L25.8367 25.8333V19.1591C25.7676 19.1641 25.6981 19.1667 25.6283 19.1667C24.93 19.1667 24.2549 18.9108 23.7533 18.4719C23.2518 18.9108 22.5767 19.1667 21.8783 19.1667C21.18 19.1667 20.5049 18.9108 20.0033 18.4719C19.5018 18.9108 18.8267 19.1667 18.1283 19.1667C17.43 19.1667 16.7549 18.9108 16.2533 18.4719C15.7518 18.9108 15.0767 19.1667 14.3783 19.1667C14.3086 19.1667 14.2391 19.1641 14.17 19.1591Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25368">
  <rect width="40" height="46" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const otherVisited: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25372)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#0A0A7B"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#0F0FC0"/>
  <path d="M18.75 18.25H21.75V20.5H18.75V18.25Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.25 16.1247C10.7959 15.7274 10.5 15.1636 10.5 14.5V14.3965L11.5256 10.75H24.4744L25.5 14.3965V14.5C25.5 15.1636 25.2041 15.7275 24.75 16.1247V24.25L11.25 24.25V16.1247ZM23.3381 12.25L23.9937 14.581C23.9451 14.8973 23.6177 15.25 23.0625 15.25C22.4592 15.25 22.125 14.8336 22.125 14.5H20.625C20.625 14.8336 20.2908 15.25 19.6875 15.25C19.0842 15.25 18.75 14.8336 18.75 14.5H17.25C17.25 14.8336 16.9158 15.25 16.3125 15.25C15.7092 15.25 15.375 14.8336 15.375 14.5H13.875C13.875 14.8336 13.5408 15.25 12.9375 15.25C12.3823 15.25 12.0549 14.8973 12.0063 14.581L12.6619 12.25L23.3381 12.25ZM12.75 16.7432V22.75H14.25V19H17.25V22.75L23.25 22.75V16.7432C23.1878 16.7477 23.1253 16.75 23.0625 16.75C22.434 16.75 21.8264 16.5197 21.375 16.1247C20.9236 16.5197 20.316 16.75 19.6875 16.75C19.059 16.75 18.4514 16.5197 18 16.1247C17.5486 16.5197 16.941 16.75 16.3125 16.75C15.684 16.75 15.0764 16.5197 14.625 16.1247C14.1736 16.5197 13.566 16.75 12.9375 16.75C12.8747 16.75 12.8122 16.7477 12.75 16.7432Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25372">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const vineyardEnabled: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25426)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#2A2AE9"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#344EF4"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M20.25 10.81H15.75V12.31H17.25V14.3362C16.8087 14.081 16.2964 13.9349 15.75 13.9349C14.0931 13.9349 12.75 15.278 12.75 16.9349C12.75 18.4348 13.8507 19.6776 15.2885 19.8996C15.1035 20.2892 15 20.7249 15 21.1849C15 22.8417 16.3431 24.1849 18 24.1849C19.6569 24.1849 21 22.8417 21 21.1849C21 20.7249 20.8965 20.2892 20.7115 19.8996C22.1493 19.6776 23.25 18.4348 23.25 16.9349C23.25 15.278 21.9069 13.9349 20.25 13.9349C19.7036 13.9349 19.1913 14.081 18.75 14.3362V12.31L20.25 12.31V10.81ZM15.75 18.4349C16.5784 18.4349 17.25 17.7633 17.25 16.9349C17.25 16.1064 16.5784 15.4349 15.75 15.4349C14.9216 15.4349 14.25 16.1064 14.25 16.9349C14.25 17.7633 14.9216 18.4349 15.75 18.4349ZM18.75 16.9349C18.75 17.7633 19.4216 18.4349 20.25 18.4349C21.0784 18.4349 21.75 17.7633 21.75 16.9349C21.75 16.1064 21.0784 15.4349 20.25 15.4349C19.4216 15.4349 18.75 16.1064 18.75 16.9349ZM18 22.6849C18.8284 22.6849 19.5 22.0133 19.5 21.1849C19.5 20.3564 18.8284 19.6849 18 19.6849C17.1716 19.6849 16.5 20.3564 16.5 21.1849C16.5 22.0133 17.1716 22.6849 18 22.6849Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25426">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const vineyardSelected: MapIcon = {
  icon: `<svg width="40" height="46" viewBox="0 0 40 46" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25405)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24.2245 39.553C33.2414 37.6142 40 29.5964 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 29.9295 7.23602 38.1689 16.7225 39.7328L20.4191 45.8382L24.2245 39.553Z" fill="#2B2B2B"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M22.4933 12.573H17.4933V14.2397H19.16V16.491C18.6697 16.2074 18.1005 16.0451 17.4933 16.0451C15.6524 16.0451 14.16 17.5375 14.16 19.3784C14.16 21.045 15.383 22.4259 16.9805 22.6726C16.775 23.1054 16.66 23.5896 16.66 24.1006C16.66 25.9416 18.1524 27.434 19.9933 27.434C21.8343 27.434 23.3267 25.9416 23.3267 24.1006C23.3267 23.5896 23.2117 23.1054 23.0061 22.6725C24.6036 22.4259 25.8267 21.045 25.8267 19.3784C25.8267 17.5375 24.3343 16.0451 22.4933 16.0451C21.8862 16.0451 21.317 16.2074 20.8267 16.491V14.2397L22.4933 14.2397V12.573ZM17.4933 21.0451C18.4138 21.0451 19.16 20.2989 19.16 19.3784C19.16 18.4579 18.4138 17.7118 17.4933 17.7118C16.5729 17.7118 15.8267 18.4579 15.8267 19.3784C15.8267 20.2989 16.5729 21.0451 17.4933 21.0451ZM20.8267 19.3784C20.8267 20.2989 21.5729 21.0451 22.4933 21.0451C23.4138 21.0451 24.16 20.2989 24.16 19.3784C24.16 18.4579 23.4138 17.7118 22.4933 17.7118C21.5729 17.7118 20.8267 18.4579 20.8267 19.3784ZM19.9933 25.7673C20.9138 25.7673 21.66 25.0211 21.66 24.1006C21.66 23.1802 20.9138 22.434 19.9933 22.434C19.0729 22.434 18.3267 23.1802 18.3267 24.1006C18.3267 25.0211 19.0729 25.7673 19.9933 25.7673Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25405">
  <rect width="40" height="46" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

const vineyardVisted: MapIcon = {
  icon: `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_1503_25415)">
  <path d="M18 36C27.9411 36 36 27.9411 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 27.9411 8.05887 36 18 36Z" fill="#0A0A7B"/>
  <path d="M18 35C27.9411 35 36 27.165 36 17.5C36 7.83502 27.9411 0 18 0C8.05887 0 0 7.83502 0 17.5C0 27.165 8.05887 35 18 35Z" fill="#0F0FC0"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M20.25 10.81H15.75V12.31H17.25V14.3362C16.8087 14.081 16.2964 13.9349 15.75 13.9349C14.0931 13.9349 12.75 15.278 12.75 16.9349C12.75 18.4348 13.8507 19.6776 15.2885 19.8996C15.1035 20.2892 15 20.7249 15 21.1849C15 22.8417 16.3431 24.1849 18 24.1849C19.6569 24.1849 21 22.8417 21 21.1849C21 20.7249 20.8965 20.2892 20.7115 19.8996C22.1493 19.6776 23.25 18.4348 23.25 16.9349C23.25 15.278 21.9069 13.9349 20.25 13.9349C19.7036 13.9349 19.1913 14.081 18.75 14.3362V12.31L20.25 12.31V10.81ZM15.75 18.4349C16.5784 18.4349 17.25 17.7633 17.25 16.9349C17.25 16.1064 16.5784 15.4349 15.75 15.4349C14.9216 15.4349 14.25 16.1064 14.25 16.9349C14.25 17.7633 14.9216 18.4349 15.75 18.4349ZM18.75 16.9349C18.75 17.7633 19.4216 18.4349 20.25 18.4349C21.0784 18.4349 21.75 17.7633 21.75 16.9349C21.75 16.1064 21.0784 15.4349 20.25 15.4349C19.4216 15.4349 18.75 16.1064 18.75 16.9349ZM18 22.6849C18.8284 22.6849 19.5 22.0133 19.5 21.1849C19.5 20.3564 18.8284 19.6849 18 19.6849C17.1716 19.6849 16.5 20.3564 16.5 21.1849C16.5 22.0133 17.1716 22.6849 18 22.6849Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_1503_25415">
  <rect width="36" height="36" fill="white"/>
  </clipPath>
  </defs>
  </svg>
  `,
};

export type PinState = 'enabled' | 'selected' | 'visited';
export type PinType = 'restaurant' | 'bar' | 'vineyard' | 'other';

const businessTypeToPinType: Dictionary<PinType> = {
  restaurant: 'restaurant',
  'pop up': 'restaurant',
  bakery: 'restaurant',
  cafe: 'restaurant',
  market: 'restaurant',
  'culinary tours': 'restaurant',
  'tea house': 'restaurant',
  'supper club': 'restaurant',
  farm: 'restaurant',
  'coffee shop': 'restaurant',
  creamery: 'restaurant',
  bar: 'bar',
  brewery: 'bar',
  distillery: 'bar',
  winery: 'vineyard',
};

export const getPinTypeFromBusinessType = (businessType?: string): PinType => {
  if (!businessType?.trim()) {
    return 'restaurant';
  }

  const pinType = businessTypeToPinType[businessType.toLowerCase()];

  if (!pinType) {
    return 'other';
  }

  return pinType;
};

export const getBusinessPinIcon = (
  scaledSize = 36,
  state: PinState = 'enabled',
  type: PinType = 'restaurant'
): google.maps.Icon => {
  return {
    url: 'data:image/svg+xml;base64,' + btoa(getPinAsset(state, type)?.icon),
    scaledSize: new google.maps.Size(scaledSize, scaledSize),
    // centered, 6px padding between icon and label
    labelOrigin: new google.maps.Point(scaledSize / 2, scaledSize + 6),
  };
};

const getPinAsset = (state: PinState, type: PinType) => {
  switch (state) {
    case 'enabled':
      return getEnabledPin(type);
    case 'selected':
      return getSelectedPin(type);
    case 'visited':
      return getVisitedPin(type);
    default:
      assertNever(state);
  }
};

const getEnabledPin = (type: PinType) => {
  switch (type) {
    case 'restaurant':
      return restaurantEnabled;
    case 'bar':
      return barEnabled;
    case 'vineyard':
      return vineyardEnabled;
    case 'other':
      return otherEnabled;
    default:
      assertNever(type);
  }
};

const getSelectedPin = (type: PinType) => {
  switch (type) {
    case 'restaurant':
      return restaurantSelected;
    case 'bar':
      return barSelected;
    case 'vineyard':
      return vineyardSelected;
    case 'other':
      return otherSelected;
    default:
      assertNever(type);
  }
};

const getVisitedPin = (type: PinType) => {
  switch (type) {
    case 'restaurant':
      return restaurantVisited;
    case 'bar':
      return barVisited;
    case 'vineyard':
      return vineyardVisted;
    case 'other':
      return otherVisited;
    default:
      assertNever(type);
  }
};

export function mapDotIcon(
  scaledSize = 24,
  color1 = 'rgba(32,32,192,0.3)',
  color2 = '#FFF',
  color3 = 'rgba(32,32,192,1)'
): google.maps.Icon {
  return {
    url:
      'data:image/svg+xml;base64,' +
      btoa(
        `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\
<circle cx="12" cy="12" r="10" fill="${color1}"/>\
<circle cx="12" cy="12" r="5" fill="${color2}" />\
<circle cx="12" cy="12" r="3.5" fill="${color3}" />\
</svg>`
      ),
    anchor: new google.maps.Point(scaledSize * 0.5, scaledSize * 0.5),
    scaledSize: new google.maps.Size(scaledSize, scaledSize),
  };
}
